import { useEffect, useId, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const CATEGORY_IDS = [
  'terms',
  'privacy',
  'bonuses',
  'payments',
  'responsible',
  'kyc',
  'aml',
  'fairplay',
  'self_exclusion',
  'disputes',
  'sports',
] as const;

type CategoryId = (typeof CATEGORY_IDS)[number];

function isCategoryId(value: string | undefined): value is CategoryId {
  return Boolean(value && (CATEGORY_IDS as readonly string[]).includes(value));
}

export function RulesPage() {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['legal', 'common']);
  const [menuOpen, setMenuOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const listboxId = useId();

  const activeId: CategoryId = isCategoryId(categoryId) ? categoryId : 'terms';

  useEffect(() => {
    if (!categoryId) {
      navigate('/qaydalar/terms', { replace: true });
      return;
    }
    if (!isCategoryId(categoryId)) {
      navigate('/qaydalar/terms', { replace: true });
    }
  }, [categoryId, navigate]);

  useEffect(() => {
    setMenuOpen(false);
  }, [activeId]);

  useEffect(() => {
    if (!menuOpen) return;

    function onPointerDown(event: MouseEvent | TouchEvent) {
      const target = event.target as Node | null;
      if (rootRef.current && target && !rootRef.current.contains(target)) {
        setMenuOpen(false);
      }
    }

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') setMenuOpen(false);
    }

    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen || !listRef.current) return;
    const active = listRef.current.querySelector<HTMLElement>('[data-active="true"]');
    active?.scrollIntoView({ block: 'nearest' });
  }, [menuOpen, activeId]);

  const sections = t(`legal:categories.${activeId}.sections`, { returnObjects: true }) as
    | Array<{ heading: string; body: string }>
    | string;
  const list = Array.isArray(sections) ? sections : [];

  function selectCategory(id: CategoryId) {
    setMenuOpen(false);
    if (id === activeId) return;
    navigate(`/qaydalar/${id}`);
    window.requestAnimationFrame(() => {
      document.getElementById('rules-document')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  function goBack() {
    if (window.history.length > 1) navigate(-1);
    else navigate('/profile');
  }

  function scrollTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  return (
    <div className="page rules-page rules-page--single">
      <div className="rules-page-head">
        <button type="button" className="rules-back-btn" onClick={goBack} aria-label={t('common:back', { defaultValue: 'Back' })}>
          <span aria-hidden="true">‹</span>
        </button>
        <h1>{t('legal:navTitle')}</h1>
      </div>

      <div className={`rules-select${menuOpen ? ' is-open' : ''}`} ref={rootRef}>
        <button
          type="button"
          className="rules-select-trigger"
          aria-haspopup="listbox"
          aria-expanded={menuOpen}
          aria-controls={listboxId}
          onClick={() => setMenuOpen((open) => !open)}
        >
          <span>{t(`legal:categories.${activeId}.title`)}</span>
          <span className="rules-select-chevron" aria-hidden="true">
            ↕
          </span>
        </button>

        {menuOpen ? (
          <ul
            id={listboxId}
            className="rules-select-menu"
            role="listbox"
            aria-label={t('common:rulesSelectAria')}
            ref={listRef}
          >
            {CATEGORY_IDS.map((id) => {
              const selected = id === activeId;
              return (
                <li key={id} role="option" aria-selected={selected} data-active={selected ? 'true' : 'false'}>
                  <button type="button" className={`rules-select-option${selected ? ' is-active' : ''}`} onClick={() => selectCategory(id)}>
                    <span>{t(`legal:categories.${id}.title`)}</span>
                    {selected ? <span className="rules-select-check" aria-hidden="true">✓</span> : null}
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>

      <article id="rules-document" className="rules-document" key={`rules-${i18n.resolvedLanguage || i18n.language}-${activeId}`}>
        <h2 className="rules-document-title">{t(`legal:categories.${activeId}.title`)}</h2>
        <p className="muted-note">{t(`legal:categories.${activeId}.summary`)}</p>
        <div className="rules-sections">
          {list.map((section, index) => (
            <section key={`${i18n.resolvedLanguage || i18n.language}-${activeId}-${index}`} className="rules-section">
              <h3>{section.heading}</h3>
              <p>{section.body}</p>
            </section>
          ))}
        </div>
      </article>

      <button type="button" className="rules-scroll-top" onClick={scrollTop} aria-label={t('common:scrollToTop')}>
        <span aria-hidden="true">↑</span>
      </button>
    </div>
  );
}
